import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("email"); // "email" | "phone"
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: "patient",
    specialization: "", city: "Patna",
  });
  const [loading, setLoading] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleSignIn = () => {
    const redirectUrl = window.location.origin + "/patient";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (mode === "phone") delete payload.email;  // backend synthesizes phone-based email
      const u = await register(payload);
      toast.success(`Account created — welcome ${u.name.split(" ")[0]}`);
      nav(u.role === "doctor" ? "/doctor" : "/patient");
    } catch (err) {
      toast.error(formatError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-12">
      <div className="text-center mb-6">
        <img src="/logo-full.webp" alt="MedHOne — One Platform, Every Care" className="h-20 mx-auto object-contain" data-testid="register-brand-logo" />
      </div>
      <Card className="p-8 rounded-2xl">
        <h1 className="font-display text-3xl font-bold">Create your MedHOne account</h1>
        <p className="text-slate-600 text-sm mt-1">One platform, every care — consultations, tests, medicines & emergency</p>

        <div className="mt-5 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => setMode("email")} data-testid="signup-email-tab"
            className={`py-2 rounded-lg text-sm font-semibold ${mode === "email" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-700"}`}>
            Email signup
          </button>
          <button type="button" onClick={() => setMode("phone")} data-testid="signup-phone-tab"
            className={`py-2 rounded-lg text-sm font-semibold ${mode === "phone" ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-700"}`}>
            Phone-only signup
          </button>
        </div>

        <Button type="button" variant="outline" onClick={googleSignIn} data-testid="google-signup"
          className="w-full mt-3 border-slate-300 flex items-center justify-center gap-2">
          <svg className="w-4 h-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l-.02.15 3.5 2.7.24.02c2.2-2 3.4-5 3.4-8.6z"/><path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.8-2.9c-1 .7-2.4 1.2-4.1 1.2-3.1 0-5.8-2.1-6.8-5l-.14.01-3.6 2.8-.05.13C3.4 21.3 7.4 24 12 24z"/><path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.1-1.7.4-2.4l-.01-.16-3.7-2.8-.12.06C.5 8.3 0 10.1 0 12s.5 3.7 1.4 5.3l3.8-2.9z"/><path fill="#EA4335" d="M12 4.6c2.3 0 3.8 1 4.7 1.8l3.4-3.3C18 1.2 15.2 0 12 0 7.4 0 3.4 2.7 1.4 6.7l3.8 2.9c1-2.9 3.7-5 6.8-5z"/></svg>
          Continue with Google
        </Button>
        <p className="text-[11px] text-slate-500 text-center mt-2">Google sign-up creates a patient account instantly — no password needed.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Full name</Label>
              <Input data-testid="reg-name" value={form.name} onChange={set("name")} required />
            </div>
            <div>
              <Label>Phone{mode === "phone" && <span className="text-red-500"> *</span>}</Label>
              <Input data-testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+91 …" required={mode === "phone"} />
            </div>
          </div>
          {mode === "email" && (
            <div>
              <Label>Email</Label>
              <Input data-testid="reg-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
          )}
          <div>
            <Label>Password</Label>
            <Input data-testid="reg-password" type="password" value={form.password} onChange={set("password")} required minLength={6} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="reg-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="patient">Patient</SelectItem>
                  <SelectItem value="doctor">Doctor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City</Label>
              <Input data-testid="reg-city" value={form.city} onChange={set("city")} />
            </div>
          </div>
          {form.role === "doctor" && (
            <div>
              <Label>Specialization</Label>
              <Input data-testid="reg-spec" value={form.specialization} onChange={set("specialization")} placeholder="e.g. Cardiologist" />
            </div>
          )}
          <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-500 text-white">
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <div className="text-sm text-center text-slate-600 mt-4">
          Already have an account? <Link className="text-teal-700 font-semibold" to="/login">Sign in</Link>
        </div>
      </Card>
    </div>
  );
}
